/**
 * Mirrors SearchController#resolve_mls_numbers ID normalization:
 * comma/space split, reject blanks, cap 200, strip single quotes, uppercase.
 *
 * @param {string} listingIds - e.g. expanded range string from _expandRanges (comma-separated)
 * @returns {string[]}
 */
export function normalizeListingIdsFromExpandedString(listingIds) {
  if (listingIds == null || String(listingIds).trim() === '') {
    return [];
  }
  const parts = String(listingIds)
    .replace(/,/g, ' ')
    .split(/\s+/)
    .filter((p) => p.length > 0)
    .slice(0, 200);
  return parts.map((id) => id.replace(/'/g, '').toUpperCase());
}

/**
 * Expands comma-separated MLS # ranges (e.g. A100..A105) into a
 * comma-delimited list of IDs, capped at 200 entries.
 *
 * @param {string} input
 * @returns {string}
 */
export function expandMlsRanges(input) {
  const maxRangeSize = 200;
  const parts = String(input || '').split(/\s*,\s*/);
  const expanded = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    if (part === '') continue;

    const rangeMatch = part.match(/^(.*?)(\d+)\.\.(.*?)(\d+)$/);
    if (rangeMatch) {
      let prefixA = rangeMatch[1];
      let prefixB = rangeMatch[3];

      if (prefixB === '') {
        prefixB = prefixA;
      }
      if (prefixA !== prefixB) {
        expanded.push(part);
        continue;
      }

      const startStr = rangeMatch[2];
      const padLength = startStr.length;
      let start = parseInt(startStr, 10);
      let end = parseInt(rangeMatch[4], 10);
      if (start > end) {
        const tmp = start;
        start = end;
        end = tmp;
      }
      if ((end - start) > maxRangeSize) {
        end = start + maxRangeSize;
      }
      for (let n = start; n <= end; n++) {
        if (expanded.length >= maxRangeSize) break;
        expanded.push(prefixA + String(n).padStart(padLength, '0'));
      }
    } else {
      expanded.push(part);
    }
    if (expanded.length >= maxRangeSize) break;
  }

  return expanded.join(',');
}

/**
 * Stable signature for MLS hidden fields (ListingKey and ListingId) for change detection.
 *
 * @param {HTMLElement} containerEl - hidden container element
 * @returns {string}
 */
export function mlsResolutionSignatureForHiddenContainer(containerEl) {
  if (!containerEl) {
    return '';
  }
  const keyInputs = containerEl.querySelectorAll(
    'input[name="search_fields[ListingKey][value][]"]'
  );
  const idInputs = containerEl.querySelectorAll(
    'input[name="search_fields[ListingId][value][]"]'
  );
  const keys = Array.from(keyInputs)
    .map((el) => el.value)
    .filter(Boolean)
    .sort();
  const ids = Array.from(idInputs)
    .map((el) => el.value)
    .filter(Boolean)
    .sort();
  if (keys.length === 0 && ids.length === 0) {
    return '';
  }
  return `K:${keys.join('|')}|I:${ids.join('|')}`;
}
